import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink, Outlet, useLocation } from "react-router";
import { logout } from "@/auth/actions";
import { useMe } from "@/auth/use-me";
import { useHasSystemAccess } from "@/auth/roles";
import { isTobisAiEnabled, isTobisDetached, isTobisStandalone } from "@/lib/tobis-mode";
import { useAppName } from "@/lib/use-app-name";
import { useHydrateThemeFromSystemSettings } from "@/lib/use-system-theme";
import { AssistantDrawer } from "@/components/chat/AssistantDrawer";
import { AssistantLauncher } from "@/components/chat/AssistantLauncher";
import { ChatDock } from "@/components/chat/ChatDock";
import { MessagesLauncher } from "@/components/chat/MessagesLauncher";
import { Avatar } from "@/components/common/Avatar";
import { menuItemClasses } from "@/components/design/menu-item-classes";
import { NotificationCenter } from "@/components/layout/NotificationCenter";
import { TourHintLauncher } from "@/components/layout/TourHintLauncher";
import { SaveBar } from "@/components/layout/SaveBar";
import { SearchBar } from "@/components/layout/SearchBar";
import { SessionExpiredNotice } from "@/components/layout/SessionExpiredNotice";
import { UnsavedGuard } from "@/components/layout/UnsavedGuard";
import { PendingEditsProvider } from "@/lib/pending-edits";
import { useApplyThemePrefs } from "@/stores/theme-prefs";

// AppShell - node-by-node port of workshop's components/layout/AppShell.tsx.
// Persistent left sidebar on lg+, mobile drawer below; topbar with the
// inline SearchBar + AccountMenu + Logout; main outlet centred.
//
// ChatDock is not ported yet - Phase 5c reskins the chat module to
// workshop's ChatDock / ChatWindow / MessagesBanner pattern. Until then
// the existing tech-stack ChatOverlay sits in the same DOM position.

// Pillar `label`s are product/brand names — never translated, identical in
// both locales (ADR-0013 product-name carve-out), so they stay inline literals.
// Submenu items carry a message id + inline default; feature names (Service,
// Community, Pins, Map, Watch, Play) resolve to the same value in en/de, only
// functional chrome (Settings, Questionnaires) actually differs per locale.
interface NavItem {
  to: string;
  labelId: string;
  defaultMessage: string;
}

interface Pillar {
  id: string;
  label: string;
  to: string;
  activePath?: string;
  items: readonly NavItem[];
}

const PILLARS: readonly Pillar[] = [
  {
    id: "save",
    label: "SAVE",
    to: "/save",
    items: [
      { to: "/save/basics", labelId: "nav.profile", defaultMessage: "Profile" },
      { to: "/save/documents", labelId: "nav.service", defaultMessage: "Documents" },
      { to: "/save/contact", labelId: "nav.community", defaultMessage: "Community" },
      { to: "/save/pinboard", labelId: "nav.pins", defaultMessage: "Pins" },
    ],
  },
  {
    id: "grow",
    label: "GROW",
    to: "/grow",
    items: [
      { to: "/grow/map", labelId: "nav.map", defaultMessage: "Map" },
      { to: "/grow/watch", labelId: "nav.watch", defaultMessage: "Watch" },
      { to: "/grow/play", labelId: "nav.play", defaultMessage: "Play" },
    ],
  },
  { id: "share", label: "SHARE", to: "/share", items: [] },
  { id: "match", label: "MATCH", to: "/match", items: [] },
  {
    // Prototyp tobis: Filmrechte-Datenplattform. Register und Filmografien, plus
    // die Ingest-Pipeline unter Sources. Wie ADMIN nur mit Systemrolle sichtbar,
    // solange es ein Prototyp ist.
    id: "tobis",
    label: "MOVIE",
    to: "/tobis/people",
    activePath: "/tobis",
    items: [
      { to: "/tobis/lab", labelId: "nav.tobisLab", defaultMessage: "Lab" },
      { to: "/tobis/people", labelId: "nav.tobisPeople", defaultMessage: "People" },
      { to: "/tobis/titles", labelId: "nav.tobisTitles", defaultMessage: "Titles" },
      { to: "/tobis/market", labelId: "nav.tobisMarket", defaultMessage: "Market" },
    ],
  },
  {
    // System-settings surface (branding, reference data, questionnaires);
    // links straight to the settings page. Visible only with a system role.
    id: "admin",
    label: "ADMIN",
    to: "/admin/settings",
    activePath: "/admin",
    items: [],
  },
];

// Saeulen, die eine Systemrolle voraussetzen.
const SYSTEM_ONLY_PILLARS = new Set(["admin", "tobis"]);

// Kernprodukt-Saeulen, die in der tobis-Standalone-Instanz (featureFlags.
// tobisStandalone, siehe TOBIS_STANDALONE_MODE in apps/bff/.env dieser
// Worktree) keinen Sinn ergeben: das Match/Save/Grow/Share-Menue gehoert zum
// restlichen Smycles-Produkt, nicht zum isolierten Filmrechte-Prototyp.
const STANDALONE_HIDDEN_PILLARS = new Set(["save", "grow", "share", "match"]);

// Ohne genesis (featureFlags.tobisDetached) faellt zusaetzlich ADMIN weg: die
// Systemeinstellungen lesen und schreiben genesis.
const DETACHED_HIDDEN_PILLARS = new Set(["admin"]);

const CHROME_TRANSITION =
  "background-color 1600ms cubic-bezier(0.22, 1, 0.36, 1), " +
  "color 1600ms cubic-bezier(0.22, 1, 0.36, 1), " +
  "border-color 1600ms cubic-bezier(0.22, 1, 0.36, 1)";

function isActivePath(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

function GearIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.076.124l1.217-.456a1.125 1.125 0 0 1 1.369.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.248a1.125 1.125 0 0 1 1.37-.49l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
    </svg>
  );
}

// Navigation column shared by the persistent desktop sidebar and the mobile
// drawer. Hover-opened submenus stay open (no collapse on mouse-out) but are
// bound to the current route: after a navigation they reset and only the
// active pillar stays open. Purely derived, no effect needed. `onNavigate`
// lets the mobile drawer close itself when a link is followed.
function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const intl = useIntl();
  const location = useLocation();
  const appName = useAppName();
  const hasSystemAccess = useHasSystemAccess();
  const tobisStandalone = isTobisStandalone();
  const tobisDetached = isTobisDetached();
  // The Systemeinstellungen pillar is visible only with a system-settings role.
  const pillars = PILLARS.filter(
    (pillar) =>
      (!SYSTEM_ONLY_PILLARS.has(pillar.id) || hasSystemAccess) &&
      !(tobisStandalone && STANDALONE_HIDDEN_PILLARS.has(pillar.id)) &&
      !(tobisDetached && DETACHED_HIDDEN_PILLARS.has(pillar.id)),
  );
  const [opened, setOpened] = useState<{ path: string; ids: Set<string> }>(() => ({
    path: location.pathname,
    ids: new Set<string>(),
  }));
  const openIds = opened.path === location.pathname ? opened.ids : null;

  return (
    <>
      <NavLink
        to="/"
        onClick={() => {
          setOpened({ path: "/", ids: new Set<string>() });
          onNavigate?.();
        }}
        // The /tour-all intro points at the logo as "the way to the main page".
        data-assistant-anchor="nav.home"
        className="px-5 pb-8 pt-6 font-serif text-[26px] leading-none tracking-tight text-ink"
      >
        {appName}
      </NavLink>
      <nav
        aria-label={intl.formatMessage({ id: "nav.mainLandmark", defaultMessage: "Main" })}
        className="flex-1 space-y-1 overflow-y-auto px-3"
      >
        {pillars.map((pillar) => {
          const active = isActivePath(location.pathname, pillar.activePath ?? pillar.to);
          const open = active || (openIds?.has(pillar.id) ?? false);
          return (
            <div
              key={pillar.id}
              onMouseEnter={
                pillar.items.length > 0
                  ? () =>
                      setOpened((prev) => {
                        const base = prev.path === location.pathname ? prev.ids : new Set<string>();
                        if (base.has(pillar.id)) return prev;
                        return { path: location.pathname, ids: new Set(base).add(pillar.id) };
                      })
                  : undefined
              }
            >
              <NavLink
                to={pillar.to}
                onClick={onNavigate}
                // Assistant onboarding anchor (/tour-all pillar intro highlights + lasting marker).
                data-assistant-anchor={`nav.${pillar.id}`}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[14px] uppercase tracking-[0.18em] transition ${
                  active ? "font-medium text-ink" : "text-ink/55 hover:font-medium hover:text-ink"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                    active ? "bg-ink" : "bg-transparent"
                  }`}
                />
                <span>{pillar.label}</span>
              </NavLink>
              {pillar.items.length > 0 ? (
                <div
                  className={`grid transition-[grid-template-rows] duration-[450ms] ease-out ${
                    open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div
                      className={`mb-1 ml-7 mt-1 space-y-0.5 border-l border-ink/10 pl-3 transition-opacity duration-[450ms] ease-out ${
                        open ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {pillar.items.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={onNavigate}
                          className={({ isActive }) =>
                            `block rounded-md px-3 py-2 text-left text-[14px] transition ${
                              isActive ? "font-medium text-ink" : "text-ink/55 hover:font-medium hover:text-ink"
                            }`
                          }
                        >
                          {intl.formatMessage({ id: item.labelId, defaultMessage: item.defaultMessage })}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
    </>
  );
}

// Topbar account: shows just the first name + avatar; hover/focus opens a
// card with full name, email, Settings, and Logout. Name + photo come from the
// real Person record (useMe), not the JWT claims. The pt-1 on the card wrapper
// bridges the gap to the trigger so hover doesn't tear off when crossing.
export function AccountMenu() {
  const intl = useIntl();
  const me = useMe();
  // Profil (/save/basics) und Kontoeinstellungen lesen und schreiben genesis.
  const detached = isTobisDetached();
  const accountLabel = intl.formatMessage({ id: "account.label", defaultMessage: "Account" });
  const firstName = me.firstName || accountLabel;
  const fullName = me.fullName || firstName;
  const person = {
    name: fullName,
    avatarContent: me.avatarContent ?? null,
    avatarMime: me.avatarMime ?? null,
  };
  const identity = (
    <>
      <Avatar person={person} size="lg" />
      <div className="min-w-0">
        <div className="truncate text-[14px] font-medium text-ink">{fullName}</div>
        {me.email ? (
          <div className="break-all text-[12px] leading-snug text-ink/55">{me.email}</div>
        ) : null}
      </div>
    </>
  );

  return (
    <div className="group relative">
      <button
        type="button"
        className="flex max-w-[160px] items-center gap-2 rounded-full py-1 pl-1 pr-2.5 text-sm text-ink/70 transition hover:bg-ink/[0.06] group-focus-within:bg-ink/[0.06]"
      >
        <Avatar person={person} size="sm" />
        <span className="truncate">{firstName}</span>
      </button>

      <div className="invisible absolute right-0 top-full z-40 w-64 translate-y-1 pt-1 opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <div className="rounded-md bg-surface p-4 shadow-lg ring-1 ring-ink/10">
          {detached ? (
            <div className="-m-2 flex items-center gap-3 p-2">{identity}</div>
          ) : (
            <NavLink
              to="/save/basics"
              className="-m-2 flex items-center gap-3 rounded-md p-2 transition hover:bg-ink/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/15"
            >
              {identity}
            </NavLink>
          )}

          <div className="mt-3 border-t border-ink/5 pt-2">
            {detached ? null : (
              <NavLink to="/settings" className={menuItemClasses()}>
                <span className="shrink-0">
                  <GearIcon />
                </span>
                <span className="min-w-0 flex-1 truncate">
                  <FormattedMessage id="nav.settings" defaultMessage="Settings" />
                </span>
              </NavLink>
            )}
            <button
              type="button"
              onClick={() => void logout()}
              className={menuItemClasses()}
            >
              <span className="shrink-0">
                <LogoutIcon />
              </span>
              <span className="min-w-0 flex-1 truncate text-left">
                <FormattedMessage id="nav.logout" defaultMessage="Logout" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LogoutIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"
      />
    </svg>
  );
}

export function AppShell() {
  const intl = useIntl();
  const tobisStandalone = isTobisStandalone();
  // Ohne genesis/agent-service: Assistent, Chat, Benachrichtigungen und Tour
  // haetten kein Backend und bleiben deshalb ganz weg.
  const tobisDetached = isTobisDetached();

  useApplyThemePrefs();
  useHydrateThemeFromSystemSettings();

  // Mobile navigation: under lg the sidebar is hidden and the hamburger
  // opens it as a drawer. Each drawer link closes via onNavigate; the
  // overlay (z-50) covers all other nav triggers, so closing on navigate
  // is enough. Escape also closes.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = () => setMobileNavOpen(false);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  return (
    <PendingEditsProvider>
      <div
        data-theme-mode="light"
        data-depth="off"
        className="flex h-screen bg-page text-ink"
        style={{ transition: CHROME_TRANSITION }}
      >
        {/* Persistent sidebar from lg+ - below that the drawer takes over. print:hidden
            here and on every other chrome element below: a printed page (Tobis Lab's
            "Export as PDF" via window.print(), see TobisLabSessionPage.tsx) should show
            only the routed report content, not nav/topbar/assistant/chat. */}
        <aside className="hidden w-[200px] shrink-0 flex-col lg:flex print:hidden">
          <SidebarNav />
        </aside>

        {/* Mobile drawer + backdrop. Stays mounted for the slide animation;
            without visibility it doesn't catch clicks (pointer-events-none). */}
        <div
          className={`fixed inset-0 z-50 lg:hidden print:hidden ${mobileNavOpen ? "" : "pointer-events-none"}`}
          aria-hidden={!mobileNavOpen}
        >
          <div
            onClick={closeMobileNav}
            className={`absolute inset-0 bg-ink/30 transition-opacity duration-300 ${
              mobileNavOpen ? "opacity-100" : "opacity-0"
            }`}
          />
          <aside
            className={`absolute inset-y-0 left-0 flex w-[260px] max-w-[80%] flex-col bg-page shadow-xl transition-transform duration-300 ease-out ${
              mobileNavOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <SidebarNav onNavigate={closeMobileNav} />
          </aside>
        </div>

        <div className="relative flex min-w-0 flex-1 flex-col">
          <header className="relative z-30 bg-page print:hidden" style={{ transition: CHROME_TRANSITION }}>
            <div className="flex h-14 items-center gap-3 px-4 sm:gap-6 sm:px-6">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                aria-label={intl.formatMessage({ id: "nav.openMenu", defaultMessage: "Open menu" })}
                className="-ml-1 shrink-0 rounded-md p-2 text-ink/70 transition hover:bg-ink/[0.06] hover:text-ink lg:hidden"
              >
                <MenuIcon />
              </button>
              {tobisStandalone ? (
                <div className="min-w-0 flex-1" />
              ) : (
                <div className="flex min-w-0 flex-1 justify-center">
                  {/* data-assistant-anchor: the /tour-all intro presents the topbar items. */}
                  <div className="w-full max-w-[520px]" data-assistant-anchor="topbar.search">
                    <SearchBar />
                  </div>
                </div>
              )}
              <div className="flex shrink-0 items-center gap-3">
                {isTobisAiEnabled() ? (
                  <span className="inline-flex" data-assistant-anchor="topbar.assistant">
                    <AssistantLauncher />
                  </span>
                ) : null}
                {tobisDetached ? null : (
                  <>
                    <span className="inline-flex" data-assistant-anchor="topbar.chat">
                      <MessagesLauncher />
                    </span>
                    <span className="inline-flex" data-assistant-anchor="topbar.notifications">
                      <NotificationCenter />
                    </span>
                    <span className="inline-flex" data-assistant-anchor="topbar.help">
                      <TourHintLauncher />
                    </span>
                  </>
                )}
                <span className="inline-flex" data-assistant-anchor="topbar.user">
                  <AccountMenu />
                </span>
              </div>
            </div>
          </header>
          <main className="@container min-h-0 flex-1 overflow-auto print:overflow-visible">
            <Outlet />
          </main>
          {tobisDetached ? null : (
            <div className="print:hidden">
              <ChatDock />
            </div>
          )}
        </div>
        <div className="print:hidden">
          {isTobisAiEnabled() ? <AssistantDrawer /> : null}
          <SaveBar />
          <UnsavedGuard />
        </div>
        <SessionExpiredNotice />
      </div>
    </PendingEditsProvider>
  );
}
